import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AgreementType,
  AssignableMember,
  BillingAuditTimeEntry,
  BillingClass,
  CreateTimeEntryInput,
  PaginatedResult,
  ServiceCategory,
  SortDirection,
  TimeEntry,
  TimeEntryAgreementOption,
  TimeEntryCustomerOption,
  TimeEntrySummary,
  UpdateBillingAuditTimeEntryInput,
  UpdateTimeEntryInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface TimeEntryListParams {
  page: number;
  pageSize: number;
  memberId?: string;
  memberIds?: string[];
  projectId?: string;
  agreementId?: string;
  agreementType?: AgreementType;
  agreementSubtypeId?: string;
  projectServiceCategoryId?: string;
  customerId?: string;
  serviceCategoryId?: string;
  billingClassId?: string;
  notes?: string;
  ticketId?: string;
  timesheetId?: string;
  billable?: boolean;
  fromDate?: string;
  toDate?: string;
  sortBy?: string;
  sortDir?: SortDirection;
}
const timeEntryKeys = {
  all: ['time-entries'] as const,
  list: (params: TimeEntryListParams) => ['time-entries', 'list', params] as const,
  summary: (params: TimeEntrySummaryParams) => ['time-entries', 'summary', params] as const,
  detail: (id: string) => ['time-entries', 'detail', id] as const,
};

/** Customer search for the guided log-time form (gated on `time`, not `customers`). */
export function useTimeEntryCustomerOptions(
  search: string,
  enabled = true,
): UseQueryResult<TimeEntryCustomerOption[]> {
  return useQuery({
    queryKey: ['time-entries', 'customer-options', search] as const,
    queryFn: () =>
      api.get<TimeEntryCustomerOption[]>(
        `/time-entries/customer-options${buildQuery({ search: search || undefined })}`,
      ),
    enabled,
  });
}

/** A customer's active agreements for the guided log-time form (gated on `time`). */
export function useTimeEntryAgreementOptions(
  customerId: string,
  includeId: string,
  enabled = true,
): UseQueryResult<TimeEntryAgreementOption[]> {
  return useQuery({
    queryKey: ['time-entries', 'agreement-options', customerId, includeId] as const,
    queryFn: () =>
      api.get<TimeEntryAgreementOption[]>(
        `/time-entries/agreement-options${buildQuery({
          customerId: customerId || undefined,
          includeId: includeId || undefined,
        })}`,
      ),
    enabled: enabled && Boolean(customerId || includeId),
  });
}

export function useTimeEntries(
  params: TimeEntryListParams,
): UseQueryResult<PaginatedResult<TimeEntry>> {
  return useQuery({
    queryKey: timeEntryKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<TimeEntry>>(
        `/time-entries${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          memberId: params.memberId,
          memberIds: params.memberIds?.join(','),
          projectId: params.projectId,
          agreementId: params.agreementId,
          agreementType: params.agreementType,
          agreementSubtypeId: params.agreementSubtypeId,
          projectServiceCategoryId: params.projectServiceCategoryId,
          customerId: params.customerId,
          serviceCategoryId: params.serviceCategoryId,
          billingClassId: params.billingClassId,
          notes: params.notes,
          ticketId: params.ticketId,
          timesheetId: params.timesheetId,
          billable: params.billable,
          fromDate: params.fromDate,
          toDate: params.toDate,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useTimeEntry(id: string | null): UseQueryResult<TimeEntry> {
  return useQuery({
    queryKey: id ? timeEntryKeys.detail(id) : ['time-entries', 'detail', null],
    queryFn: () => api.get<TimeEntry>(`/time-entries/${id}`),
    enabled: !!id,
  });
}

/** Filters shared by the list and the summary (the summary reflects the same set). */
export interface TimeEntrySummaryParams {
  memberId?: string;
  memberIds?: string[];
  customerId?: string;
  projectId?: string;
  agreementId?: string;
  agreementType?: AgreementType;
  agreementSubtypeId?: string;
  projectServiceCategoryId?: string;
  serviceCategoryId?: string;
  billingClassId?: string;
  notes?: string;
  billable?: boolean;
  fromDate?: string;
  toDate?: string;
}

export function useTimeEntriesSummary(
  params: TimeEntrySummaryParams,
): UseQueryResult<TimeEntrySummary> {
  return useQuery({
    queryKey: timeEntryKeys.summary(params),
    queryFn: () =>
      api.get<TimeEntrySummary>(
        `/time-entries/summary${buildQuery({
          memberId: params.memberId,
          memberIds: params.memberIds?.join(','),
          customerId: params.customerId,
          projectId: params.projectId,
          agreementId: params.agreementId,
          agreementType: params.agreementType,
          agreementSubtypeId: params.agreementSubtypeId,
          projectServiceCategoryId: params.projectServiceCategoryId,
          serviceCategoryId: params.serviceCategoryId,
          billingClassId: params.billingClassId,
          notes: params.notes,
          billable: params.billable,
          fromDate: params.fromDate,
          toDate: params.toDate,
        })}`,
      ),
  });
}

export interface WriteOffTimeEntryArgs {
  id: string;
  writtenOff: boolean;
}

/**
 * Writes off (or restores) a time entry. Open to time approvers, unlike the invoice-side
 * write-off which needs invoice permissions.
 */
export function useWriteOffTimeEntry(): UseMutationResult<
  TimeEntry,
  unknown,
  WriteOffTimeEntryArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, writtenOff }: WriteOffTimeEntryArgs) =>
      api.post<TimeEntry>(`/time-entries/${id}/write-off`, { writtenOff }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useCreateTimeEntry(): UseMutationResult<TimeEntry, unknown, CreateTimeEntryInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTimeEntryInput) => api.post<TimeEntry>('/time-entries', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export interface UpdateTimeEntryArgs {
  id: string;
  input: UpdateTimeEntryInput;
}

export function useUpdateTimeEntry(): UseMutationResult<TimeEntry, unknown, UpdateTimeEntryArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTimeEntryArgs) =>
      api.patch<TimeEntry>(`/time-entries/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export function useDeleteTimeEntry(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/time-entries/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export interface UpdateTimeEntryBillingArgs {
  id: string;
  input: UpdateBillingAuditTimeEntryInput;
}

/**
 * Updates a time entry's billing status (billed-externally / written-off / external
 * reference) via the admin billing-audit endpoint. That path detaches a written-off
 * entry from its invoice and recomputes totals, and is not gated by timesheet status.
 */
export function useUpdateTimeEntryBilling(): UseMutationResult<
  BillingAuditTimeEntry,
  unknown,
  UpdateTimeEntryBillingArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTimeEntryBillingArgs) =>
      api.patch<BillingAuditTimeEntry>(`/billing-audit/time-entries/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Reference data for the time-entry form
// ---------------------------------------------------------------------------

/** Members the user may log time against (reuses the ticket assignable-members list). */
export function useMemberOptions(): UseQueryResult<AssignableMember[]> {
  return useQuery({
    queryKey: ['tickets', 'assignable-members'] as const,
    queryFn: () => api.get<AssignableMember[]>('/tickets/assignable-members'),
  });
}

export function useServiceCategoryOptions(): UseQueryResult<ServiceCategory[]> {
  return useQuery({
    queryKey: ['service-categories', 'options'] as const,
    queryFn: () => api.get<ServiceCategory[]>('/service-categories/options'),
  });
}

export function useBillingClassOptions(): UseQueryResult<BillingClass[]> {
  return useQuery({
    queryKey: ['billing-classes', 'options'] as const,
    queryFn: () => api.get<BillingClass[]>('/billing-classes/options'),
  });
}
